<template>
    <div class="login-form">
        <v-tabs v-model="mode"
                background-color="transparent"
                class="mb-4"
                dark
                grow>
            <v-tab to="login">
                Login
            </v-tab>
            <v-tab to="signup">
                Signup
            </v-tab>
        </v-tabs>

        <div
            v-if="mode === 'login'"
            :class="{active: mode === 'login'}">
            <form
                id="login-form"
                method="post"
                @submit.prevent="login">
                <v-text-field v-model="loginForm.username"
                              label="Username"
                              background-color="white"
                              hide-details
                              name="username"
                              class="mb-3"
                              outlined>
                </v-text-field>

                <v-text-field v-model="loginForm.password"
                              label="Password"
                              background-color="white"
                              hide-details
                              name="password"
                              type="password"
                              class="mb-3"
                              outlined>
                </v-text-field>

                <v-btn block
                       color="primary"
                       type="submit">
                    Login
                </v-btn>

                <p class="mt-4 mb-1 text-center">
                    <router-link to="/forgot">
                        Forgotten your password?
                    </router-link>
                </p>
            </form>
        </div>

        <div
            v-if="mode === 'signup'"
            :class="{active: mode === 'signup'}">
            <form
                id="signup-form"
                method="post"
                @submit.prevent="signup">
                <v-text-field v-model="signupForm.username"
                              label="Username"
                              background-color="white"
                              hide-details
                              name="username"
                              class="mb-3"
                              outlined>
                </v-text-field>

                <v-text-field v-model="signupForm.email"
                              label="Email Address"
                              background-color="white"
                              hide-details
                              name="email"
                              class="mb-3"
                              outlined>
                </v-text-field>

                <v-text-field v-model="signupForm.password"
                              label="Password"
                              background-color="white"
                              hide-details
                              name="password"
                              type="password"
                              class="mb-3"
                              outlined>
                </v-text-field>

                <v-btn block
                       color="primary"
                       type="submit">
                    Signup
                </v-btn>
            </form>
        </div>
    </div>
</template>

<script>
import { gaEvent } from '@antriver/vue-site-utils/src/util/analytics';
import { loginFromResponse, showSignupCompleteModal } from '@antriver/vue-site-utils/src/util/auth';
import debounce from 'lodash/debounce';
import Vue from 'vue';
import { api, authTokenStore } from '@/services/api';
import { hideLoader, showLoader } from '@/utils/loading';
import { showAlert } from '@/utils/alert';

export default Vue.extend({
    props: {
        showSignupCompleteModal: {
            type: Boolean,
            default: true,
        },
        redirectOnLogin: {
            type: Boolean,
            default: false,
        },
        initialMode: {
            type: String,
            default: 'login',
        },
        showChangeMode: {
            type: Boolean,
            default: true,
        },
        signupBtnText: {
            type: String,
            default: 'Create Account',
        },
        signupBtnIcon: {
            type: String,
            default: 'fa-plus',
        },
    },

    data() {
        return {
            /**
             * login or signup
             */
            mode: this.initialMode,

            loginForm: {
                loading: false,
                username: '',
                password: '',
            },

            signupForm: {
                focused: false,
                loading: false,
                username: '',
                email: '',
                password: '',
                errors: null,
            },

            recaptchaResponse: null,
        };
    },

    watch: {
        initialMode(newVal) {
            this.mode = newVal;
        },
    },

    mounted() {
        this.debouncedValidateSignup = debounce(() => {
            this.$root.$options.$api.post(
                'users/validate',
                {
                    username: this.signupForm.username,
                    email: this.signupForm.email,
                    password: this.signupForm.password,
                },
            )
                .then((response) => {
                    this.$set(this.signupForm, 'errors', response.errors);
                });
        }, 500);
    },

    methods: {
        setMode(mode) {
            this.mode = mode;
            this.$emit('mode-changed', mode);
        },

        login() {
            showLoader();

            this.loginForm.loading = true;
            api.post(
                'auth',
                {
                    username: this.loginForm.username,
                    password: this.loginForm.password,
                    extraAuthInfo: 1,
                },
            )
                .then((response) => {
                    gaEvent(this, ' Users', 'Login');

                    loginFromResponse(
                        authTokenStore,
                        this.$store,
                        response,
                        this.$router,
                        !!this.redirectOnLogin,
                    );
                    this.$emit('logged-in', response);

                    // addUnverifiedEmailAlert(this.$store, response.userGlobalOptions);
                })
                .catch(({ message, response }) => {
                    // Besides the regular error you expect (wrong username / password)
                    // There are 3 special cases when logging in:
                    // BannedUserException
                    //      Emits loginFailed event. Should redirect to the user's page.
                    // DeactivatedUserException
                    //      Handled here. We just display a link to reactivate the account.
                    // UnverifiedUserException
                    //      Not sure. Could either handle here or emit loginFailed. There is some logic in the app
                    //      code for handling this that could be reused. Requires connection to socket to listen for
                    //      the user verified event.

                    // Handle unverified and deactivated users here
                    if (response && response.type) {
                        switch (response.type) {
                            case 'BannedUserException':
                                // this.$emit('login-failed', response);
                                this.$router.push(response.user.url);
                                return;

                            case 'DeactivatedUserException':
                                this.$router.push({
                                    path: '/reactivate',
                                    query: {
                                        username: response.user.username,
                                        jwt: response.jwt,
                                    },
                                });
                                return;

                            case 'UnverifiedUserException':
                                this.goToVerification(response);
                                return;
                        }
                    }

                    // For other reasons (probably incorrect password)
                    showAlert(message);
                })
                .finally(() => {
                    hideLoader();
                    this.loginForm.loading = false;
                });
        },

        signupInputFocused() {
            this.signupForm.focused = true;
        },

        validateSignup() {
            this.debouncedValidateSignup();
        },

        getSignupData() {
            return {
                username: this.signupForm.username,
                email: this.signupForm.email,
                password: this.signupForm.password,
                recaptcha: this.recaptchaResponse,
                extraAuthInfo: 1,
            };
        },

        signup() {
            this.signupForm.loading = true;
            this.signupReal();
        },

        signupReal() {
            showLoader();
            this.signupForm.loading = true;
            api.post(
                'users',
                this.getSignupData(),
            )
                .then((response) => {
                    gaEvent(this, ' Users', 'Signup');

                    response.user.wasRecentlyCreated = true;

                    loginFromResponse(
                        authTokenStore,
                        this.$store,
                        response,
                        this.$router,
                        !!this.redirectOnLogin,
                    );

                    this.$emit('logged-in', response);
                    this.$emit('account-created', response);

                    // if (this.showSignupCompleteModal !== false) {
                    //     showSignupCompleteModal(this, response.user);
                    // }
                })
                .catch((error) => {
                    this.showAlert(error instanceof Error ? error.message : error);
                })
                .finally(() => {
                    this.hideLoader();
                    this.signupForm.loading = false;
                    this.resetRecaptcha();
                });
        },

        onRecaptchaResponse(r) {
            this.signupForm.loading = false;
            this.recaptchaResponse = r;
            this.signupReal();
        },

        executeRecaptcha(data, hashKey) {
            return this.$refs.recaptcha ? this.$refs.recaptcha.execute(data, hashKey) : null;
        },

        resetRecaptcha() {
            return this.$refs.recaptcha ? this.$refs.recaptcha.reset() : null;
        },

        goToVerification(response, recentlyCreated = false) {
            this.$router.push({
                path: '/verification',
                query: {
                    email: response.userEmail,
                    recentlyCreated,
                    verificationId: response.emailVerification ? response.emailVerification.id : null,
                    username: response.user.username,
                    jwt: response.jwt,
                },
            });
        },
    },
});
</script>
